import Accordion from 'client/components/accordion/accordion';
import Panel from 'client/components/accordion/panel';
import BenefitList from 'client/components/benefit/visionList';
import BenefitItem from 'client/components/benefit/visionListItem';
import LinkButton from 'client/components/button/linkButton';
import TwoColLayout from 'client/components/grid/twoColLayout';
import Calculation from 'client/components/plan/calculation';
import StrideTip from 'client/components/strideTip';
import SurveyQuestion from 'client/components/surveyQuestion';
import Translate from 'client/components/translate';
import 'client/styles/detailItem.scss';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { getVisionList } from 'web-core/src/plan-benefits';
import { Tracking } from 'web-core/src/tracking';

const benefitsTranslatePrefix = 'vision.planDetails.sections';
const pdfUrl = 'https://cdn.bfldr.com/KY2W7EFL/at/rc8w4gr28q6nq3z3fqks33/3601_VISION_Stride_Health_-_Summary.pdf';

const SaveMoreInfo = ({ translatePrefix, cta, onClick }) => (
  <div className="info margin-bottom-2">
    <div className="header">
      <Translate path={`${translatePrefix}.title`} />
    </div>
    <div className="description">
      <Translate path={`${translatePrefix}.description`} />
    </div>
    {cta && (
      <div className="margin">
        <a
          href={cta}
          className="bold"
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClick}>
          <Translate path={`${translatePrefix}.cta`} />
        </a>
      </div>
    )}
  </div>
);
SaveMoreInfo.propTypes = {
  translatePrefix: PropTypes.string.isRequired,
  cta: PropTypes.string,
  onClick: PropTypes.func,
};
SaveMoreInfo.defaultProps = {
  cta: null,
  onClick: null,
};

const propTypes = {
  plan: PropTypes.object.isRequired,
  context: PropTypes.string.isRequired,
  translations: PropTypes.shape().isRequired,
};

class DetailVision extends Component {
  getBenefits({ serviceTypes, inNetwork = true }) {
    return this.props.plan.benefits.filter(b => (
      serviceTypes.indexOf(b.serviceType) > -1 && b.inNetwork === inNetwork
    ));
  }

  getBenefitSections() {
    return [
      {
        title: {
          path: `${benefitsTranslatePrefix}.basic.title`,
        },
        renderContent: this.renderBasicInfo.bind(this),
      },
      {
        title: {
          path: `${benefitsTranslatePrefix}.glasses.title`,
        },
        renderContent: this.renderBenefitGlasses.bind(this),
      },
      {
        title: {
          path: `${benefitsTranslatePrefix}.contacts.title`,
        },
        renderContent: this.renderBenefitContacts.bind(this),
      },
      {
        title: {
          path: `${benefitsTranslatePrefix}.savings.title`,
        },
        renderContent: this.renderSavings.bind(this),
      },
      {
        title: {
          path: `${benefitsTranslatePrefix}.outOfNetwork.title`,
        },
        renderContent: this.renderBenefitOon.bind(this),
      },
      {
        title: {
          path: `${benefitsTranslatePrefix}.renaissanceCompliance.title`,
        },
        renderContent: this.renderRenaissanceCompliancePanel.bind(this),
      },
    ];
  }

  track(name, props) {
    Tracking.track(`${_.capitalize(this.props.context)}: ${name}`, props);
  }

  renderStrideTip({ section, translateProps, planUrl }) {
    return (
      <StrideTip
        description={translateProps}
        planUrl={planUrl}
        onClick={() => {
          this.track('Plan Details: SBC', { Source: section, Type: 'vision' });
        }} />
    );
  }

  renderBasicInfo() {
    const wellnessVisits = this.getBenefits({ serviceTypes: ['exam_wellvision'] });
    const wellnessValue = getVisionList({ benefits: wellnessVisits })[0];
    const contacts = this.getBenefits({ serviceTypes: ['contacts'] })[0];
    const translatePrefix = `${benefitsTranslatePrefix}.basic`;
    const benefitPrefix = `${translatePrefix}.benefits`;
    const leftColumn = (
      <div>
        <h5 className="detail-list-title">&nbsp;</h5>
        <div className="detail-list">
          <BenefitItem
            benefit={wellnessValue}
            label={{ path: `${benefitPrefix}.exam_wellvision.label` }} />
        </div>
      </div>
    );

    const rightColumn = (
      <div>
        <h5 className="detail-list-title">
          <Translate path={`${translatePrefix}.listHeader`} />
        </h5>
        <div className="detail-list">
          <BenefitItem
            label={{ path: `${benefitPrefix}.eyeware_basic.label` }}
            description={{ path: `${benefitPrefix}.eyeware_basic.description` }}
            value={{ path: `${benefitPrefix}.eyeware_basic.value`, using: contacts }} />
        </div>
        <div className="legalese">
          <Translate path={`${translatePrefix}.disclaimer`} />
        </div>
      </div>
    );

    return (
      <div>
        <div className="panel-padding">
          <TwoColLayout
            sectionClassName="detail-lists"
            leftContent={leftColumn}
            rightContent={rightColumn} />
        </div>
        {this.renderStrideTip({
          section: 'Basic Info',
          translateProps: {
            path: `${translatePrefix}.strideTip`,
          },
        })}
      </div>
    );
  }

  renderSurvey({ id, translatePrefix, section }) {
    return (
      <SurveyQuestion
        style={{ margin: '1rem' }}
        id={id}
        text={{ path: `${translatePrefix}.question` }}
        clickHandler={value => this.track('Plan Details: Feedback', {
          Action: _.capitalize(value),
          Type: 'vision',
          Source: section,
        })}
        buttons={['yes', 'no'].map(value => ({
          text: {
            path: `${translatePrefix}.${value}`,
          },
          value,
          className: 'secondary',
        }))} />
    );
  }

  renderBenefitGlasses() {
    const translatePrefix = `${benefitsTranslatePrefix}.glasses`;
    const benefitPrefix = `${translatePrefix}.benefits`;
    const exampleTranslatePrefix = `${translatePrefix}.example`;
    const calcTranslatePrefix = `${exampleTranslatePrefix}.calculations`;
    const leftBenefits = this.getBenefits({
      serviceTypes: [
        'glasses_prescription',
        'glasses_frame',
      ],
    });

    const rightBenefits = this.getBenefits({
      serviceTypes: [
        'lenses_progressive_standard',
        'lenses_progressive_premium',
        'lenses_progressive_custom',
        'lenses_enhancements_other',
      ],
    });

    const leftColumn = (
      <div>
        <h5 className="detail-list-title">&nbsp;</h5>
        <BenefitList
          benefits={leftBenefits}
          translatePrefix={benefitPrefix} />
        <div className="legalese">
          <Translate path={`${translatePrefix}.disclaimer`} />
        </div>
      </div>
    );
    const rightColumn = (
      <div>
        <h5 className="detail-list-title">
          <Translate path={`${translatePrefix}.listHeader`} />
        </h5>
        <BenefitList
          benefits={rightBenefits}
          translatePrefix={benefitPrefix} />
      </div>
    );

    // TODO: if/when we get more plans, make the numbers pull from benefit info
    const calculations = [
      {
        key: 'frames',
        lines: [
          {
            math: '$200 + 0',
            label: {
              path: `${calcTranslatePrefix}.framesLenses`,
            },
          },
          {
            math: '- $120',
            label: {
              path: `${calcTranslatePrefix}.insPays`,
            },
          },
          {
            math: '$80',
            isResult: true,
          },
        ],
      },
      {
        key: 'discounts',
        lines: [
          {
            math: '- $16',
            label: {
              path: `${calcTranslatePrefix}.savings`,
            },
          },
          {
            math: '+ $25',
            label: {
              path: `${calcTranslatePrefix}.copay`,
            },
          },
          {
            math: '$89.00',
            isResult: true,
          },
        ],
      },
    ];

    return (
      <div>
        <div className="panel-padding">
          <TwoColLayout
            sectionClassName="detail-lists"
            leftContent={leftColumn}
            rightContent={rightColumn} />
        </div>
        <h4 className="text-center margin-top-2" style={{ paddingBottom: '2rem' }}>
          <Translate path={`${exampleTranslatePrefix}.title`} />
        </h4>
        <div className="stride-info flex-column margin-top-2">
          <div className="header-image">
            <img src="https://images.contentful.com/heg7gyzix6dq/11L3y90CU0Ka0WOCqCkWGy/1d9e912c562cdf5371c9a850af772be2/persona-jake.svg" alt="" />
          </div>
          <p className="margin-bottom-2 center-col-small text-center">
            <Translate path={`${exampleTranslatePrefix}.description`} />
          </p>
          <div className="callout">
            <div className="content">
              <div className="label">
                <Translate path={`${exampleTranslatePrefix}.calloutLabel`} />
              </div>
              <div className="number">$89.00</div>
            </div>
          </div>
          <h4 className="margin-top-2 margin-bottom">
            <Translate path={`${calcTranslatePrefix}.title`} />
          </h4>
          <Calculation calculations={calculations} />
          <hr />
          {this.renderSurvey({
            id: 'vision-detail-glasses',
            translatePrefix: `${exampleTranslatePrefix}.survey`,
            section: 'Glasses',
          })}
        </div>
        {this.renderStrideTip({
          section: 'Glasses',
          translateProps: {
            path: `${translatePrefix}.strideTip`,
            using: { amountMax: 120 },
          },
          planUrl: pdfUrl,
        })}
      </div>
    );
  }

  renderBenefitContacts() {
    const translatePrefix = `${benefitsTranslatePrefix}.contacts`;
    const benefitPrefix = `${translatePrefix}.benefits`;
    const exampleTranslatePrefix = `${translatePrefix}.example`;
    const calcTranslatePrefix = `${exampleTranslatePrefix}.calculations`;
    const leftBenefits = this.getBenefits({
      serviceTypes: [
        'contacts_exam',
      ],
    });

    const rightBenefits = this.getBenefits({
      serviceTypes: [
        'contacts',
      ],
    });

    const leftColumn = (
      <div>
        <h5 className="detail-list-title">&nbsp;</h5>
        <BenefitList
          benefits={leftBenefits}
          translatePrefix={benefitPrefix} />
      </div>
    );
    // adds asterisk to amount
    rightBenefits[0].disclaimer = true;
    const rightColumn = (
      <div>
        <h5 className="detail-list-title">
          <Translate path={`${translatePrefix}.listHeader`} />
        </h5>
        <BenefitList
          benefits={rightBenefits}
          translatePrefix={benefitPrefix} />
        <div className="legalese">
          <Translate path={`${translatePrefix}.disclaimer`} />
        </div>
      </div>
    );

    // TODO: if/when we get more plans, make the numbers pull from benefit info
    const finalResult = '$90.00';
    const calculations = [
      {
        key: 'frames',
        lines: [
          {
            math: '$150',
            label: {
              path: `${calcTranslatePrefix}.contacts`,
            },
          },
          {
            math: '+ $60',
            label: {
              path: `${calcTranslatePrefix}.exam`,
            },
          },
          {
            math: '- $120',
            label: {
              path: `${calcTranslatePrefix}.insPays`,
            },
          },
          {
            math: finalResult,
            isResult: true,
          },
        ],
      },
    ];

    return (
      <div>
        <div className="panel-padding">
          <TwoColLayout
            sectionClassName="detail-lists"
            leftContent={leftColumn}
            rightContent={rightColumn} />
        </div>
        <h4 className="text-center margin-top-2" style={{ paddingBottom: '2rem' }}>
          <Translate path={`${translatePrefix}.example.title`} />
        </h4>
        <div className="stride-info flex-column pad-top-2">
          <div className="header-image">
            <img src="https://images.contentful.com/heg7gyzix6dq/2ebdTqyPWU0gy8sAeSG046/b0d9c0da128b1c18c6b3e885bf8de6d8/persona-meg.svg" alt="" />
          </div>
          <p className="pad-bottom-2 center-col-small text-center">
            <Translate path={`${exampleTranslatePrefix}.description`} />
          </p>
          <div className="callout">
            <div className="content">
              <div className="label">
                <Translate path={`${exampleTranslatePrefix}.calloutLabel`} />
              </div>
              <div className="number">{finalResult}</div>
            </div>
          </div>
          <h4 className="margin-top-2 margin-bottom">
            <Translate path={`${calcTranslatePrefix}.title`} />
          </h4>
          <Calculation calculations={calculations} />
          <hr />
          <h4 className="margin margin-bottom-2">
            <Translate path={`${exampleTranslatePrefix}.followUp.title`} />
          </h4>
          <div className="flex-row">
            <div className="text-center pad-right">
              <img src="https://images.contentful.com/heg7gyzix6dq/4XlN3suNAAs6YqKkAwokMM/0820c70533978a61850e77a16eac1ece/renew-calendar.svg" alt="" />
              <h5 className="teal-color" style={{ width: 150, paddingTop: 5 }}>
                <strong><Translate path={`${exampleTranslatePrefix}.followUp.iconLabel`} /></strong>
              </h5>
            </div>
            <div className="callout">
              <div className="content">
                <div className="label">
                  <Translate path={`${exampleTranslatePrefix}.followUp.calloutLabel`} />
                </div>
                <div className="number">$150.00</div>
              </div>
            </div>
          </div>
          <p className="margin-2 center-col-small text-center">
            <Translate path={`${exampleTranslatePrefix}.followUp.description`} />
          </p>
          <hr />
          {this.renderSurvey({
            id: 'vision-detail-contacts',
            translatePrefix: `${exampleTranslatePrefix}.survey`,
            section: 'Contacts',
          })}
        </div>
        {this.renderStrideTip({
          section: 'Contacts',
          translateProps: {
            path: `${translatePrefix}.strideTip`,
            using: leftBenefits[1],
          },
          planUrl: pdfUrl,
        })}
      </div>
    );
  }

  renderSavings() {
    const translatePrefix = `${benefitsTranslatePrefix}.savings`;
    const contentBase = _.get(this.props.translations, translatePrefix);
    const leftColumn = (
      <div>
        {['glasses', 'contacts'].map(key => <SaveMoreInfo translatePrefix={`${translatePrefix}.${key}`} key={key} />)}
      </div>
    );

    const rightColumn = (
      <div>
        <SaveMoreInfo translatePrefix={`${translatePrefix}.laser`} />
        <SaveMoreInfo
          translatePrefix={`${translatePrefix}.perks`}
          cta="https://www.vsp.com/optical-discounts.html"
          onClick={() => this.track('Plan Details: CTA', {
            Type: 'vision',
            Source: contentBase.perks.cta,
          })} />
      </div>
    );

    return (
      <div className="savings">
        <div className="panel-padding pad-top">
          <TwoColLayout leftContent={leftColumn} rightContent={rightColumn} />
        </div>
        {this.renderStrideTip({
          section: 'Savings',
          translateProps: {
            path: `${translatePrefix}.strideTip`,
          },
        })}
      </div>
    );
  }

  renderRenaissanceCompliancePanel() {
    const translatePrefix = `${benefitsTranslatePrefix}.renaissanceCompliance`;

    const leftColumn = (
      <div>
        {['notes', 'exclusions'].map(key => (
          <SaveMoreInfo translatePrefix={`${translatePrefix}.${key}`} key={key} />
        ))}
      </div>
    );

    const rightColumn = (
      <div>
        {['limitations', 'premiumRate'].map(key => (
          <SaveMoreInfo translatePrefix={`${translatePrefix}.${key}`} key={key} />
        ))}
      </div>
    );

    return (
      <div className="compliance">
        <div className="panel-padding margin-top">
          <TwoColLayout leftContent={leftColumn} rightContent={rightColumn} />
        </div>
      </div>
    );
  }

  renderBenefitOon() {
    const translatePrefix = `${benefitsTranslatePrefix}.outOfNetwork`;
    const benefitPrefix = `${translatePrefix}.benefits`;
    const leftBenefits = this.getBenefits({
      serviceTypes: [
        'exam_wellvision',
        'lenses_single_vision',
        'lenses_lined_trifocal',
        'contacts',
      ],
      inNetwork: false,
    });

    const rightBenefits = this.getBenefits({
      serviceTypes: [
        'glasses_frame',
        'lenses_lined_bifocal',
        'lenses_progressive',
      ],
      inNetwork: false,
    });

    const leftColumn = (
      <div>
        <BenefitList
          benefits={leftBenefits}
          translatePrefix={benefitPrefix} />
      </div>
    );

    const rightColumn = (
      <div>
        <BenefitList
          benefits={rightBenefits}
          translatePrefix={benefitPrefix} />
        <div className="legalese">
          <Translate path={`${translatePrefix}.disclaimer`} />
        </div>
      </div>
    );

    return (
      <div className="pad-top-2">
        <div className="panel-padding">
          <h5 className="detail-list-title">
            <Translate path={`${translatePrefix}.listHeader`} />
          </h5>
          <TwoColLayout
            sectionClassName="detail-lists"
            leftContent={leftColumn}
            rightContent={rightColumn} />
        </div>
        <StrideTip
          description={{ path: `${translatePrefix}.strideTip` }}
          onClick={() => {
            Tracking.track('Dashboard: Plan Details: Doctor Search', { Source: 'outOfNetwork', Type: 'vision' });
          }}>
          <LinkButton
            target="_blank"
            className="ghost-button margin-bottom-2 margin-top"
            href="https://www.vsp.com/find-eye-doctors.html"
            onClick={() => this.track('Plan Details: CTA', {
              Type: 'vision',
              Source: _.get(this.props.translations, `${translatePrefix}.strideTipCta`),
            })}>
            <Translate path={`${translatePrefix}.strideTipCta`} />
          </LinkButton>
        </StrideTip>
      </div>
    );
  }

  renderVspInfo() {
    const clipboardImage = 'https://images.contentful.com/heg7gyzix6dq/2delfJY016E4YwGq8oSkmu/64f1cb0c2b34faff50e02b7e79bc1946/vision-clipboard.svg';
    return (
      <div className="medium-full-width-panel">
        <div className="stride-tip top-border text-center pad-y-2">
          <div className="row">
            <div className="column medium-10 large-6 small-centered">
              <div className="medium-table v-middle">
                <div className="td medium-8 medium-text-right pad-right">
                  <p>
                    <Translate path="vision.planDetails.vsp.desc1" />
                  </p>
                  <img className="hide-for-medium" src={clipboardImage} alt="" />
                  <p>
                    <Translate path="vision.planDetails.vsp.desc2" />
                  </p>
                </div>
                <div className="td medium-4 hide-for-small-only pad-left">
                  <img src={clipboardImage} alt="" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="stride-info text-center pad-y-2">
          <div className="row">
            <div className="column medium-10 large-6 small-centered medium-flex-row">
              <div className="medium-6 medium-text-right pad-x">
                <div className="display-1">
                  <Translate path="vision.planDetails.vsp.network.doctorCount" />
                </div>
                <div className="label">
                  <strong><Translate path="vision.planDetails.vsp.network.doctorLabel" /></strong>
                </div>
              </div>
              <div className="medium-6 medium-text-left pad-x">
                <p><Translate path="vision.planDetails.vsp.network.description" /></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderCoverage() {
    const contactBenefit = this.getBenefits({ serviceTypes: ['contacts'] })[0];
    return (
      <div className="coverage text-center margin-2">
        <h3 className="normal margin-2 pad-top" style={{ fontSize: '1.4rem' }}>
          <Translate path="vision.planDetails.coverage.title" />
        </h3>
        <img src="https://images.contentful.com/heg7gyzix6dq/45JyAg9gCAsU8YygY2yWUC/31ff2c7a466682c66fc8e2df0c66819a/eyeware-choice.jpg" width="306" alt="" />
        <p className="margin-2">
          <Translate path="vision.planDetails.coverage.description" using={contactBenefit} />
        </p>
      </div>
    );
  }

  renderBenefits() {
    const benefitSections = this.getBenefitSections();

    return (
      <Accordion
        initialIndex={0}
        onChange={(state) => {
          if (state.index !== null) {
            const path = _.get(benefitSections, `[${state.index}].title.path`);
            const Source = _.get(this.props.translations, path);
            this.track(
              'Plan Details: Drawer',
              {
                Source,
                Type: 'vision',
              },
            );
          }
        }}>
        {benefitSections.map((section, index) => (
          <Panel
            key={section.title.path}
            title={<Translate path={section.title.path} />}
            ref={(node) => { if (index === 0) this.firstPanel = node; }}>
            {section.renderContent()}
          </Panel>
        ))}
      </Accordion>
    );
  }

  render() {
    return (
      <div className="detail-vision">
        {this.renderVspInfo()}
        {this.renderCoverage()}
        {this.renderBenefits()}
      </div>
    );
  }
}

DetailVision.propTypes = propTypes;

export default DetailVision;
